import type { ErrorEvent } from '@sentry/core';

const IONIC_DEPLOY_HOST = 'api.ionicjs.com';

/**
 * Live-update sync loses its network race with the webview on app resume and rejects with WebKit's
 * generic fetch message; nobody owns the promise, so it lands in Sentry as an unhandled TypeError
 * @see node_modules/cordova-plugin-ionic/dist/common.js:146 — `this.sync()` is called without `await` inside a `try/catch`, which cannot catch an async rejection
 * @see node_modules/cordova-plugin-ionic/dist/common.js:241 — the `check-device` fetch that fails
 *
 * Real fix: `IonUpdateMethod = none` in Info.plist, then drive `Deploy.sync()` ourselves — delayed
 * after resume, guarded by `navigator.onLine`, with a `catch`
 */
export const isIonicDeploySyncNetworkError = (
	event: ErrorEvent,
	error: Error | null
): boolean => {
	/** WebKit reports every failed fetch as `TypeError: Load failed`, so pair it with the plugin's own request */
	if (!error?.message.includes('Load failed')) return false;

	const isUnhandledRejection = event.exception?.values?.some(
		({ mechanism }) => mechanism?.type === 'onunhandledrejection'
	);
	const hasDeployRequest = event.breadcrumbs?.some(
		({ category, data }) =>
			category === 'fetch' &&
			typeof data?.['url'] === 'string' &&
			data['url'].includes(IONIC_DEPLOY_HOST)
	);

	return Boolean(isUnhandledRejection && hasDeployRequest);
};
